import {observer} from "mobx-react-lite";
import {useCallback, useContext, useEffect, useRef, useState} from "react";
import styled, {useTheme} from "styled-components";
import {Theme, StyledProps, CvvColors} from "../../theme";
import {uploader} from "../../proto/compiled";
import {FileUploadStore} from "../../stores/file_upload_store";
import {ApiContext} from "../../index";
import {MdClose, MdUploadFile, MdInsertDriveFile} from "react-icons/md";

const UploadContainer = styled.div<{isDragActive?: boolean, hasError?: boolean}>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 16px;
  border: 2px dashed ${({theme, isDragActive, hasError}: StyledProps & {hasError?: boolean}) =>
    hasError ? CvvColors.error : isDragActive ? theme.content50 : theme.content30};
  border-radius: 8px;
  background-color: ${({theme, isDragActive}: StyledProps) => isDragActive ? theme.content5 : 'transparent'};
  cursor: pointer;
  transition: all 200ms ease;
  min-height: 80px;

  &:hover {
    border-color: ${({theme}: StyledProps) => theme.content50};
    background-color: ${({theme}: StyledProps) => theme.content5};
  }
`;

const ErrorText = styled.span`
  margin-top: 8px;
  color: ${CvvColors.error};
  font-size: 14px;
`;

const FileDisplay = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 12px;
  border: 1px solid ${({theme}: StyledProps) => theme.content20};
  border-radius: 8px;
  background-color: ${({theme}: StyledProps) => theme.content5};
`;

const UploadWrapper = styled.div`
  padding: 4px;
`;

const CompactUploadButton = styled.div`
  display: flex;
  align-items: center;
  padding: 4px 8px;
  border: 1px solid ${({theme}: StyledProps) => theme.content30};
  border-radius: 4px;
  cursor: pointer;
  color: ${({theme}: StyledProps) => theme.content50};
  font-size: 13px;
  gap: 4px;

  &:hover {
    border-color: ${({theme}: StyledProps) => theme.content50};
    background-color: ${({theme}: StyledProps) => theme.content5};
  }
`;

const FileName = styled.a`
  flex: 1;
  margin-left: 8px;
  color: ${({theme}: StyledProps) => theme.content};
  text-decoration: none;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &:hover {
    text-decoration: underline;
  }
`;

const FileNameText = styled.span`
  flex: 1;
  margin-left: 8px;
  color: ${({theme}: StyledProps) => theme.content};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const DeleteButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({theme}: StyledProps) => theme.content50};

  &:hover {
    color: ${CvvColors.error};
  }
`;

const ProgressBar = styled.div<{progress: number}>`
  width: 100%;
  height: 4px;
  background-color: ${({theme}: StyledProps) => theme.content20};
  border-radius: 2px;
  margin-top: 8px;
  overflow: hidden;

  &::after {
    content: '';
    display: block;
    height: 100%;
    width: ${({progress}) => progress * 100}%;
    background-color: ${({theme}: StyledProps) => theme.content50};
    transition: width 200ms ease;
  }
`;

const UploadText = styled.span`
  margin-top: 8px;
  color: ${({theme}: StyledProps) => theme.content50};
  font-size: 14px;
`;

interface FileUploadInputProps {
  file?: uploader.IUFile;
  onFileChanged: (file?: uploader.IUFile) => void;
  extensions?: string[];
  label?: string;
  compact?: boolean;
}

export const FileUploadInput = observer((props: FileUploadInputProps) => {
  const api = useContext(ApiContext);
  const theme = useTheme() as Theme;
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploadStore] = useState(() => new FileUploadStore(
    api,
    props.extensions ? new Set(props.extensions) : undefined
  ));
  const [isDragActive, setIsDragActive] = useState(false);

  useEffect(() => {
    return () => uploadStore.dispose();
  }, [uploadStore]);

  const handleFiles = useCallback(async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const result = await uploadStore.uploadFile(Array.from(files));
    if (result) {
      props.onFileChanged(result);
    }
  }, [uploadStore, props]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragActive(false);
    handleFiles(e.dataTransfer.files);
  }, [handleFiles]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragActive(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragActive(false);
  }, []);

  const handleClick = useCallback(() => {
    inputRef.current?.click();
  }, []);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    handleFiles(e.target.files);
    // Reset input so same file can be selected again
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  }, [handleFiles]);

  const handleDelete = useCallback(() => {
    uploadStore.cancel();
    props.onFileChanged(undefined);
  }, [uploadStore, props]);

  // Build accept string from extensions
  const acceptString = props.extensions
    ? props.extensions.map(ext => `.${ext}`).join(',')
    : undefined;

  // If we have a successfully uploaded file
  if (props.file) {
    const fileName = props.file.name || 'Datoteka';
    const fileUrl = props.file.src;

    if (props.compact) {
      return (
        <FileDisplay style={{padding: '4px 8px'}}>
          <MdInsertDriveFile size={16} color={theme.content30} />
          {fileUrl ? (
            <FileName href={fileUrl} target="_blank" rel="noopener noreferrer" style={{marginLeft: '4px', fontSize: '13px'}}>
              {fileName}
            </FileName>
          ) : (
            <FileNameText style={{marginLeft: '4px', fontSize: '13px'}}>{fileName}</FileNameText>
          )}
          <DeleteButton onClick={handleDelete} type="button" style={{padding: '2px'}}>
            <MdClose size={16} />
          </DeleteButton>
        </FileDisplay>
      );
    }

    return (
      <UploadWrapper>
        <FileDisplay>
          <MdInsertDriveFile size={24} color={theme.content30} />
          {fileUrl ? (
            <FileName href={fileUrl} target="_blank" rel="noopener noreferrer">
              {fileName}
            </FileName>
          ) : (
            <FileNameText>{fileName}</FileNameText>
          )}
          <DeleteButton onClick={handleDelete} type="button">
            <MdClose size={20} />
          </DeleteButton>
        </FileDisplay>
      </UploadWrapper>
    );
  }

  // If currently uploading (no error yet)
  if (uploadStore.started && !uploadStore.done && !uploadStore.error) {
    const fileName = uploadStore.file?.name || 'Datoteka';

    if (props.compact) {
      return (
        <FileDisplay style={{padding: '4px 8px'}}>
          <MdInsertDriveFile size={16} color={theme.content30} />
          <FileNameText style={{marginLeft: '4px', fontSize: '13px'}}>{fileName}</FileNameText>
          <ProgressBar progress={uploadStore.progress || 0} style={{marginTop: 0, marginLeft: '8px', width: '60px'}} />
        </FileDisplay>
      );
    }

    return (
      <UploadWrapper>
        <FileDisplay>
          <MdInsertDriveFile size={24} color={theme.content30} />
          <FileNameText>{fileName}</FileNameText>
          <ProgressBar progress={uploadStore.progress || 0} />
        </FileDisplay>
      </UploadWrapper>
    );
  }

  // Empty state or error state - show upload zone
  const hasError = !!uploadStore.error;

  // Compact mode - simple button
  if (props.compact) {
    return (
      <>
        <input
          ref={inputRef}
          type="file"
          accept={acceptString}
          onChange={handleInputChange}
          style={{display: 'none'}}
        />
        <CompactUploadButton onClick={handleClick}>
          <MdUploadFile size={16} />
          {hasError ? 'Napaka - poskusi znova' : 'Izberi datoteko'}
        </CompactUploadButton>
      </>
    );
  }

  return (
    <UploadWrapper>
      <input
        ref={inputRef}
        type="file"
        accept={acceptString}
        onChange={handleInputChange}
        style={{display: 'none'}}
      />
      <UploadContainer
        isDragActive={isDragActive}
        hasError={hasError}
        onClick={handleClick}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
      >
        <MdUploadFile size={32} color={hasError ? CvvColors.error : theme.content30} />
        {hasError ? (
          <ErrorText>Napaka pri nalaganju. Poskusite znova.</ErrorText>
        ) : (
          <UploadText>
            {props.label || 'Kliknite ali povlecite datoteko'}
          </UploadText>
        )}
        {props.extensions && (
          <UploadText style={{fontSize: '12px'}}>
            ({props.extensions.map(e => e.toUpperCase()).join(', ')})
          </UploadText>
        )}
      </UploadContainer>
    </UploadWrapper>
  );
});
